import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Text,
    VStack
} from '@chakra-ui/react';
import GuidedTour from '../walkthrough/components/GuidedTour';
import { TIME_WINDOWS_TOUR_ID, timeWindowsTourSteps } from '../walkthrough/tours/time-windows-tour';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import FormModal from '../../components/FormModal';
import ErrorState from '../../components/states/ErrorState';
import PageCard from '../../components/page-card';
import { translate } from '../../i18n';
import { CreateTimeWindowGroupDto, TimeWindowGroupDto } from '../../models/time-window/dto/time-window.dto';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../_shared/ScopePrompt';
import { classifyRead } from '../_shared/readState';
import { useCreateTimeWindowGroup } from './hooks/useCreateTimeWindowGroup';
import { useGetTimeWindowGroups } from './hooks/useGetTimeWindowGroups';
import { TimeWindowFormModal } from './TimeWindowFormModal';
import { TimeWindowList } from './TimeWindowList';

interface TimeWindowGroupFormData {
    name: string;
}

export function TimeWindowGroupList() {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    // The group whose slot form opens straight after it is created (MYS-70):
    // naming a group chains into its first slot instead of making the operator
    // find the new card and click "add a slot". Held here, from the POST's own
    // response, rather than looked up in `groups`, so it does not wait on (or
    // depend on) the list refetch that the create invalidates.
    const [slotGroup, setSlotGroup] = useState<Pick<TimeWindowGroupDto, 'id' | 'name'> | null>(null);
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isInitialLoading, isError } = useGetTimeWindowGroups(selectedOrganization?.id);
    // Classify on the RAW `data`, before the `?? []` below. The old
    // `data: groups = []` destructure erased the one fact that separates "we
    // asked and there are none" from "nobody asked": `useGetTimeWindowGroups`
    // is `enabled: !!organizationId`, this page had no scope guard, and React
    // Query v4 pins a disabled query at `isLoading: true` — so with no
    // organization selected the page read "Loading time windows…" forever.
    const readState = classifyRead(isError, isInitialLoading, data);
    const groups = data ?? [];
    const createMutation = useCreateTimeWindowGroup((created) => {
        setIsCreateModalOpen(false);
        reset();
        setSlotGroup({ id: created.id, name: created.name });
    });

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset
    } = useForm<TimeWindowGroupFormData>();

    const onSubmit = async (data: TimeWindowGroupFormData) => {
        const dto: CreateTimeWindowGroupDto = {
            name: data.name,
            organizationId: selectedOrganization?.id ?? '',
        };
        createMutation.mutate(dto);
    };

    if (readState === 'never-issued') {
        return (
            <PageCard title={translate('time_windows.groups')}>
                <ScopePrompt gate='organization' />
            </PageCard>
        );
    }

    if (readState === 'loading') {
        return <Box>{translate('time_windows.loading')}</Box>;
    }

    // `unreadable`, not the raw `isError`: a BACKGROUND refetch that fails over
    // groups already on screen must keep them rather than blank the page (R96-3).
    if (readState === 'unreadable') {
        return <ErrorState message={translate('time_windows.error_loading')} />;
    }

    return (
        <>
        <GuidedTour tourId={TIME_WINDOWS_TOUR_ID} steps={timeWindowsTourSteps()} />
        <PageCard
            title={translate('time_windows.groups')}
            rightHeader={
                <Button colorScheme="brand" onClick={() => setIsCreateModalOpen(true)}>
                    {translate('time_windows.create_group')}
                </Button>
            }>
            <VStack spacing={4} align="stretch" data-tour="time-window-groups">
                {groups.length === 0 && (
                    <Box p={8} textAlign="center">
                        <Text color="gray.500">{translate('time_windows.no_groups')}</Text>
                    </Box>
                )}

                {groups.map((group: TimeWindowGroupDto) => (
                    <Box key={group.id} borderWidth="1px" borderRadius="lg" m={4} p={4} data-tour="time-window-slots">
                        <Flex justifyContent="space-between" alignItems="center" mb={4}>
                            <Text fontSize="xl" fontWeight="bold">{group.name}</Text>
                        </Flex>
                        <TimeWindowList groupId={group.id} />
                    </Box>
                ))}

                <FormModal
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    title={translate('time_windows.create_group')}
                    onSubmit={() => handleSubmit(onSubmit)()}
                    submitLabel={translate('time_windows.create_group')}
                    isSubmitting={isSubmitting || createMutation.isLoading}
                >
                                    <FormControl isInvalid={!!errors.name}>
                                        <FormLabel>{translate('time_windows.group_name')}</FormLabel>
                                        <Input
                                            {...register('name', {
                                                required: translate('time_windows.group_name_required'),
                                            })}
                                            placeholder={translate('time_windows.group_name_placeholder')}
                                        />
                                    </FormControl>
                </FormModal>

                {/* Mounted only while chained, so every new group gets a fresh
                    form. Closing it is silent and leaves the empty group, as
                    before (D4). It closes only ITS group's chain: a save's
                    success calls this `onClose` from the closure captured when
                    it was sent, so a PUT the operator walked away from can
                    land after the NEXT group's chain has opened. */}
                {slotGroup && (
                    <TimeWindowFormModal
                        isOpen
                        onClose={() => {
                            const closing = slotGroup.id;
                            setSlotGroup((current) => (current?.id === closing ? null : current));
                        }}
                        groupId={slotGroup.id}
                        title={slotGroup.name || undefined}
                    />
                )}
            </VStack>
        </PageCard>
        </>
    );
}